import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { topics } from '@/mocks/topics';
import { figures } from '@/mocks/figures';
import { topicContents } from '@/mocks/topicContent';

export default function TopicDetailPage() {
  const { slug } = useParams();
  const topic = topics.find((t) => t.slug === slug);
  const content = slug ? topicContents[slug] : undefined;

  if (!topic) {
    return (
      <section className="w-full bg-background-50">
        <div className="mx-auto flex max-w-content flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到这篇专题</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            这篇专题可能已经移动或尚未收录。你可以回到专题列表，按分类重新查找。
          </p>
          <Link
            to="/topics"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-700 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
          >
            返回秘史专题
          </Link>
        </div>
      </section>
    );
  }

  const related = topics
    .filter((t) => t.id !== topic.id && t.category === topic.category)
    .slice(0, 3);
  const relatedFigureList = figures.filter((f) => topic.relatedFigures.includes(f.id));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: topic.title,
        description: topic.excerpt,
        datePublished: topic.date,
        inLanguage: 'zh-CN',
        author: { '@type': 'Organization', name: topic.author },
        publisher: { '@type': 'Organization', name: '独家黑料档案中心' },
        articleSection: topic.category,
        keywords: topic.tags.join(','),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnitsc.cn/' },
          { '@type': 'ListItem', position: 2, name: '秘史专题', item: 'https://hnitsc.cn/topics' },
          {
            '@type': 'ListItem',
            position: 3,
            name: topic.title,
            item: `https://hnitsc.cn/topics/${topic.slug}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <article className="w-full bg-background-50">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-8 md:py-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '秘史专题', to: '/topics' },
                { label: topic.category, to: `/topics?category=${encodeURIComponent(topic.category)}` },
                { label: topic.title },
              ]}
            />
            <span className="mt-6 inline-flex rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {topic.category}
            </span>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-tight">
              {topic.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {topic.excerpt}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" aria-hidden="true"></i>
                {topic.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {topic.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true"></i>
                {topic.readTime}
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-content px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
            <div className="min-w-0">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-96">
                <img
                  src={topic.cover}
                  alt={`${topic.title} 专题配图`}
                  title={`${topic.title} · 秘史专题`}
                  className="h-full w-full object-top object-cover"
                />
              </div>

              <p className="mt-8 border-l-2 border-primary-500 pl-4 font-heading text-base leading-relaxed text-foreground-900 md:text-lg">
                {content?.intro}
              </p>

              {content?.sections.map((section) => (
                <section key={section.heading} className="mt-10">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((p, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              {content?.quote && (
                <blockquote className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-500" aria-hidden="true"></i>
                  <p className="mt-2 font-heading text-base leading-relaxed text-foreground-900">
                    {content.quote}
                  </p>
                </blockquote>
              )}

              <div className="mt-10 flex flex-wrap gap-2 border-t border-background-200 pt-6">
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-800"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">相关人物档案</h2>
                <ul className="mt-4 space-y-3">
                  {relatedFigureList.map((f) => (
                    <li key={f.id} className="flex items-center gap-3">
                      <Link
                        to={`/topics?figure=${f.id}`}
                        className="flex h-14 w-14 shrink-0 overflow-hidden rounded-md bg-background-200"
                      >
                        <img
                          src={f.cover}
                          alt={`${f.name} 档案照片`}
                          title={`${f.name} · 档案人物库`}
                          className="h-full w-full object-top object-cover"
                        />
                      </Link>
                      <div className="min-w-0">
                        <p className="font-heading text-sm font-semibold text-foreground-950">
                          {f.name}
                        </p>
                        <p className="text-xs text-foreground-500">
                          {f.field} · 记录 {f.recordCount} 条
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-100 p-5">
                <h2 className="font-heading text-base font-bold text-primary-900">整理说明</h2>
                <p className="mt-3 text-xs leading-relaxed text-primary-800">
                  本篇内容依据公开可核对的材料整理，存疑之处已在正文标注。如发现条目有误或可补充的材料，
                  欢迎通过邮箱提交更正请求。
                </p>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                同类专题推荐
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((t) => (
                  <TopicCard key={t.id} topic={t} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </>
  );
}